import { Box,Stack, useMediaQuery, useTheme } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MainBox } from "./DetailsEploraRestyle.styles";
import { ActivitySelectedProvider, useActivityAndCourseSelection } from "./IndexActivitySelected.context";
import { TitleAndSubtitle } from "../../components/Commons/TitleAndSubtitle.component";
import { ActivityDotNavigation, ActivityItem, CourseActivityCard } from "../../components/CourseActivityCard";
import DomainTab from "../../components/DomainTab/DomainTab.component";
import { TeachersContent } from "../../components/LearningActiviyModal/TeachersContent.component";
import { useEditions } from "../../components/LearningActiviyModal/useEditions.hook";
import { handleTeachersAndTutors } from "../../components/LearningActiviyModal/utils";
import { NavigationSidebarRestyle } from "../../components/NavigationSidebar/NavigationSidebar.component";
import { PathCourseAccordion } from "../../components/PathCourseAccordion";
import { ResourcesContent, TabsContentCommunity } from "../../components/TabsContent";
import { Editions } from "../../components/TabsContent/Editions/Editions.component";
import { LEVEL } from "../../consts";
import type  {
  Course,
  IEdition,
  LearningActivity,
  LearningObject,
  ReferencedCourse,
  ReferencedLearningActivity,
  Root,
  Tool
} from "../../types";
import { checkCustomLogicFe, getEnvironmentVariables } from "../../utils/general";
import { useRouteParamsLo } from "../../utils/useRouteParamsLo";
import { SharedPost } from "queries/community.types";


function DeepLinkActivitySelector({ targetActivityId }: { targetActivityId?: number }) {
  const { setActivityIdSelected } = useActivityAndCourseSelection();
  const applied = useRef(false);
  useEffect(() => {
    if (applied.current || targetActivityId == null) return;
    applied.current = true;
    setActivityIdSelected(targetActivityId);
  }, [targetActivityId, setActivityIdSelected]);
  return null;
}

const DetailsEsploraRestyle = ({
  learningObject,
  idString,
  isVirtualClass,
  cousesAndActivityNumberLabel,
  sharedPosts,
  tools,
  related,
  activitiesWithEditionsRelated=[],
  learningObjectTypologyType,
  allActivitiesCousesSorted,
  setOpenModalEcm,
  isLoading,
  elementsToShow
}:{
    title?: string;
    description?: string;
    learningObject:  Course | LearningActivity | Root;
    idString: string,
    isVirtual?: boolean;
    isLoading?: boolean;
    isVirtualClass?:boolean;
    cousesAndActivityNumberLabel:string,
    sharedPosts?:SharedPost,
    tools?:Tool[] ,
    related?:LearningObject[],
    activitiesWithEditionsRelated?:ReferencedLearningActivity[] | undefined, // edizioni delle attività appartenenti al corso/percorso selezionato
    editions?:IEdition[] | undefined
    learningObjectTypologyType?:string,
    allActivitiesCousesSorted:ActivityItem[] |ReferencedCourse[],
    setOpenModalEcm?:Dispatch<SetStateAction<boolean>>,
    elementsToShow:string[]
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { customLogicFe } = getEnvironmentVariables();
  const hideKnowledgeArea = checkCustomLogicFe({ envVariable: customLogicFe, strValueToCheck: "hideKnowledgeArea" });

  const isClassroom = learningObject.learningObjectTypology === "DA_PHYSICAL_CLASS" || learningObject.learningObjectTypology === "DA_VIRTUAL_CLASS";

  const [activityCollapsed, setActivityCollapsed] = useState<null|number>(null);

  const {
    idNumber: routeIdNumber,
    courseIdNumber: routeCourseIdNumber,
    rootIdNumber: routeRootIdNumber,
    learningObjectTypology: routeTypology
  } = useRouteParamsLo();
  const hasRootContext = !isNaN(routeRootIdNumber) && routeRootIdNumber !== routeIdNumber;

  const hasCourseContext = !isNaN(routeCourseIdNumber)
    && routeCourseIdNumber !== routeIdNumber
    && routeCourseIdNumber !== routeRootIdNumber;

  const hadDeepLinkAtMount = useRef(hasRootContext || hasCourseContext);
  const openLoApplied = useRef(false);
  const openLoScrolled = useRef(false);

  const openLoTarget = useMemo<{
    courseId?: number,
    nestedActivityId?: number,
    standaloneActivityId?: number
  } | null>(() => {
    if ((!hasRootContext && !hasCourseContext) || allActivitiesCousesSorted.length === 0) return null;
    if (hasRootContext) {

      if (hasCourseContext) {
        return { courseId: routeCourseIdNumber, nestedActivityId: routeIdNumber };
      }
      if (routeTypology === LEVEL.COURSE) {
        return { courseId: routeIdNumber };
      }
      return { standaloneActivityId: routeIdNumber };
    }

    return { standaloneActivityId: routeIdNumber };
  }, [hasRootContext, hasCourseContext, routeIdNumber, routeCourseIdNumber, routeTypology,
    allActivitiesCousesSorted]);


  useEffect(() => {
    if (openLoApplied.current || !openLoTarget) return;
    openLoApplied.current = true;
    if (openLoTarget.standaloneActivityId != null) {
      const targetIndex = (allActivitiesCousesSorted as (ActivityItem | ReferencedCourse)[])
        .findIndex((lo) => lo.id === openLoTarget.standaloneActivityId);
      if (targetIndex >= 0) setActivityCollapsed(openLoTarget.standaloneActivityId);
    }
  }, [openLoTarget, allActivitiesCousesSorted]);


  useEffect(() => {
    if (!hadDeepLinkAtMount.current || !openLoTarget || openLoScrolled.current) return;
    
    const targetDomId = openLoTarget.courseId != null
      ? `path-course-accordion-${openLoTarget.courseId}`
      : `card-content-${openLoTarget.standaloneActivityId}`;

    const targetElement = document.getElementById(targetDomId);

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
      openLoScrolled.current = true;
    }
  }, [openLoTarget]);

  const teachersAndTutors = handleTeachersAndTutors(learningObject.teachers ?? [], learningObject.tutors ?? [], t);

  const editionsQueryData = useEditions(isClassroom ? learningObject as LearningActivity : null);

  const isActivity = learningObject.learningObjectTypology !== "COURSE" && learningObject.learningObjectTypology !== "PATH";
  const accordionCourseAndActivityIsVisible = allActivitiesCousesSorted.length > 0;
  const usefulLinksAreVisible = learningObject?.useful_links && learningObject.useful_links.length > 0;
  const teachersAreVisible = learningObject.teachers?.length > 0;

  //TODO manca la condizione dei tab tool link
  const sidBarNotVisible = (
    related||[]).length === 0 &&
    (tools||[])?.length === 0 &&
    learningObject.teachers?.length ===  0 &&
    (sharedPosts?.count_data_obj||0) === 0 && (activitiesWithEditionsRelated||[])?.length === 0 &&
    learningObject?.stakeholders?.length === 0 &&
     (sharedPosts?.count_data_obj||0) === 0 &&
    (learningObject.averageRatingScore||0) === 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let knowlegeArray:any = [];
  const knowlegeDomain = learningObject?.knowledge?.knowledge_domains;
  const knowlegeArea = learningObject?.knowledge?.knowledge_areas;
  const maxLength = Math.max(
    knowlegeDomain?.length || 0,
    knowlegeArea?.length || 0
  );
  for (let i = 0; i < maxLength; i++) {
    if (knowlegeDomain?.[i]) knowlegeArray.push(knowlegeDomain[i]);
    if (knowlegeArea?.[i] && !hideKnowledgeArea ) knowlegeArray.push(knowlegeArea[i]);
  }

  const capabilities = learningObject.capabilities||[];


  const isCourse = (typology: string) => typology === "COURSE";
  const isPath = (typology: string) => typology === "PATH";

  const renderComponent = () => {
    return (
      <MainBox>
        { elementsToShow.includes("DETAIL") &&
          <>
            <TitleAndSubtitle
              title={ t("what_to_expect") }
              subTitle={ learningObject?.shortDescription } />
            { knowlegeArray.length > 0 && <DomainTab array={ knowlegeArray }/> }
            { capabilities.length > 0 && <DomainTab array={ capabilities }/> }
            { !isActivity &&
              <>
                <Box sx={ {
                  marginTop:"4rem",
                  marginBottom:accordionCourseAndActivityIsVisible ? "2rem" : undefined,
                  display: "flex",
                  flexDirection: "column"
                } }>
                  <TitleAndSubtitle title={ t("contents_lo",{ lo:learningObjectTypologyType }) } subTitle={ cousesAndActivityNumberLabel } />
                </Box>
                <Box display="flex" flexDirection="column" gap="0.5rem">
                  { isPath(learningObject.learningObjectTypology) &&
                    accordionCourseAndActivityIsVisible &&
                     allActivitiesCousesSorted.map((lo, index) => {
                       const root = learningObject as Root;

                       if (isCourse(lo.learningObjectTypology)) {
                         return (
                           <PathCourseAccordion
                             key={ `${index}-${lo.title}` }
                             course={ lo as ReferencedCourse }
                             ecmRegistration={ learningObject?.ecmRegistration }
                             ecm_specialization={ learningObject?.ecm_specialization }
                             index={ index }
                             rootId={ learningObject?.id }
                             setOpenModalEcm={ setOpenModalEcm }
                             pathActivities={ root.learningActivities || [] }
                             defaultOpen={ openLoTarget?.courseId === lo.id }
                             initialActiveLoId={ openLoTarget?.courseId === lo.id
                               ? openLoTarget?.nestedActivityId ?? null
                               : null }
                           />
                         );
                       } else {
                        
                         return (
                           <CourseActivityCard
                             key={ `${lo.title}-${index}` }
                             { ...lo }
                             activity={ lo as ReferencedLearningActivity }
                             index={ index + 1 }
                             isStandAloneActivity={ true }
                             setOpenModalEcm={ setOpenModalEcm }
                             isActive={ activityCollapsed === lo.id }
                             onMouseEnter={ (id) => id && setActivityCollapsed(id) }
                             onMouseLeave={ () => setActivityCollapsed(null) }
                             teachers={ handleTeachersAndTutors(lo.teachers ?? [], lo.tutors ?? [], t) }
                             madeAttempts={ lo.madeAttempts ?? undefined }
                             editions={ (lo as ActivityItem).editions ?? undefined }
                             initialActiveLoId={ openLoTarget?.standaloneActivityId ?? undefined }
                           />
                         );
                       }
                     }) }
                  { isCourse(learningObject.learningObjectTypology) && accordionCourseAndActivityIsVisible && (
                    <ActivityDotNavigation
                      activities={ allActivitiesCousesSorted as ActivityItem[] }
                      initialActiveLoId={ openLoTarget?.standaloneActivityId ?? null }
                      courseId={ learningObject.id }
                    />
                  ) }
                </Box>
              </>
            }
          </> }
        {  isClassroom &&
          <Stack
            width={ "100%" }
            sx={ { paddingTop: "2rem",paddingBottom: usefulLinksAreVisible ? undefined : "2rem" } }
          >
            <Editions
              noPadding={ true }
              learningObject={ learningObject as LearningActivity }
              idString={ idString ?? "" }
              title={ t("editions.virtual.room") }
              description={ t("editions.virtual.room.description") }
              isVirtual={ isVirtualClass }
              teachers={ teachersAndTutors }
              editionsQueryData={ editionsQueryData }
            />
          </Stack>
        }
        {   elementsToShow.includes("INSTRUMENTS")  &&
          <Box
            component="section"
            aria-label={ t("useful_resources")  }
            sx={ {
              marginTop:"4rem",
              marginBottom:"4rem",
              display: "flex",
              flexDirection: "column"
            } }>
            <ResourcesContent
              contentLinks={ learningObject?.useful_links }
              contentTools={ tools }
              currentLO={ learningObject }
              title={ t("useful_resources") }
              subTitle={ t("doc_text") }
            />
          </Box>
        }
        { teachersAreVisible &&
          <>
            <Box
              component="section"
              aria-label={ t("teachers") }
              sx={ {
                marginTop: learningObject?.useful_links?.length > 0 ? undefined : "4rem"
              } }
            >
              <TeachersContent
                title={ t("teachers") }
                teachers={ teachersAndTutors }
              />
            </Box>
          </>
        }
        {   elementsToShow.includes("COMMUNITY")  &&
          <Box
            component="section"
            aria-label={ "posts" }
            sx={ {
              display: "flex",
              flexDirection: "column",
              paddingTop:"4rem"
            } }>
            <TitleAndSubtitle title={ t("opinions")  } />
            <TabsContentCommunity
              loId={ learningObject?.id.toString() ?? "" }
              sharedPosts={ sharedPosts }
            />
          </Box>
        }
      </MainBox>
    );
  };

  return (
    <Box
      sx={ {
        margin: !isMobile ? "2rem 2.5rem" : "2rem 1rem",
        display: "flex",
        justifyContent: "space-between",
        gap: "2rem",
        flexWrap: "wrap"
      } }
    >
      <ActivitySelectedProvider>
        <DeepLinkActivitySelector targetActivityId={ openLoTarget?.standaloneActivityId } />
        { renderComponent() }
      </ActivitySelectedProvider>
      { !sidBarNotVisible &&
        <Box width={ "24rem" }>
          <NavigationSidebarRestyle
            learningObject={ learningObject }
            related={ related || [] }
            attachedFiles={ tools?.length || 0 }
            reviews={ sharedPosts?.count_data_obj || 0 }
            activitiesWithEditionsRelated={ activitiesWithEditionsRelated||[] }
            cousesAndActivityNumberLabel={ cousesAndActivityNumberLabel }
            isLoading={ isLoading }
            elementsToShow={ elementsToShow }
          />
        </Box>
      }
    </Box>


  );
};

export default DetailsEsploraRestyle;
